import { useEffect, useState, SyntheticEvent } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContactsIcon from '@mui/icons-material/ContactsOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/CancelOutlined';
import { useAuth } from '../../context/authContext';
import { DetailHeroHeader } from '../../components/DetailHeroHeader';
import {
  approveLead,
  assignLead,
  changeLeadStatus,
  deleteLead,
  getLead,
  getLeadConversionReadiness,
  rejectLead,
  transferLead,
} from '../../api/leads';
import { finalizeStudent } from '../../api/students';
import { listLookup } from '../../api/masterData';
import { completePersonalAppointment, createPersonalAppointment } from '../../api/personalAppointments';
import { WizardValues } from '../students/wizard/wizardTypes';
import { StudentApplicationWizard } from '../students/wizard/StudentApplicationWizard';
import { AssignLeadDialog } from './AssignLeadDialog';
import { ConvertToStudentConfirmDialog } from './ConvertToStudentConfirmDialog';
import { DeleteLeadConfirmDialog } from './DeleteLeadConfirmDialog';
import { PersonalAppointmentDialog } from './PersonalAppointmentDialog';
import { RejectLeadDialog } from './RejectLeadDialog';
import { TimelineTab } from './tabs/TimelineTab';
import { FollowUpsTab } from './tabs/FollowUpsTab';
import { AttachmentsTab } from './tabs/AttachmentsTab';
import { CallsTab } from './tabs/CallsTab';
import { WhatsAppTab } from './tabs/WhatsAppTab';
import { RegistrationPaymentChip } from '../../components/payments/RegistrationPaymentChip';
import { LeadApplicationFormPanel } from './LeadApplicationFormPanel';
import { EmailTab } from './tabs/EmailTab';
import { leadBackTarget, LeadDetailState } from '../../utils/leadNavigation';

const PA_STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Scheduled',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const APPROVAL_LABELS: Record<string, string> = {
  PENDING_APPROVAL: 'Pending Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

export const LeadDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const leadId = Number(id);
  const navigate = useNavigate();
  const location = useLocation();
  // Back to wherever the lead was opened from — the Leads list with its
  // page, filters and search intact (2026-11-05), or another page.
  const back = leadBackTarget(location.state);
  const queryClient = useQueryClient();
  const { hasPermission, user } = useAuth();

  const [tab, setTab] = useState('timeline');
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeStudentId, setResumeStudentId] = useState<number | null>(null);
  const [convertConfirmOpen, setConvertConfirmOpen] = useState(false);
  const [convertConfirmError, setConvertConfirmError] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [paOpen, setPaOpen] = useState(false);
  const [paError, setPaError] = useState<string | null>(null);
  const [paSuccessMessage, setPaSuccessMessage] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [approveError, setApproveError] = useState<string | null>(null);

  useEffect(() => {
    const state = location.state as { openTab?: string; openConvert?: boolean; openAssign?: boolean } | null;
    if (state?.openTab) setTab(state.openTab);
    if (state?.openConvert) setWizardOpen(true);
    // Duplicate Lead Check's "Transfer Existing Lead" action (Lead Manager
    // only) deep-links here rather than a separate transfer flow bolted
    // onto the duplicate popup — reuses this page's existing assign/
    // transfer dialog.
    if (state?.openAssign) {
      setAssignError(null);
      setAssignOpen(true);
    }
    if (state?.openTab || state?.openConvert || state?.openAssign) {
      // One-off instructions are cleared; the way back is kept.
      navigate(location.pathname, { replace: true, state: (location.state as LeadDetailState | null)?.from ? { from: (location.state as LeadDetailState).from } : null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leadQuery = useQuery({ queryKey: ['lead', leadId], queryFn: () => getLead(leadId) });
  const statusesQuery = useQuery({
    queryKey: ['lookup', '/master-data/lead-statuses'],
    queryFn: () => listLookup('/master-data/lead-statuses'),
  });
  // Convert-to-Student workflow correction: readiness (PA/Application/
  // Registration Fee/permission) backing the "NEXT STEPS" panel — only
  // meaningful once the lead is approved, so it's not fetched before then.
  const readinessQuery = useQuery({
    queryKey: ['lead-conversion-readiness', leadId],
    queryFn: () => getLeadConversionReadiness(leadId),
    enabled: leadQuery.data?.approvalStatus === 'APPROVED',
  });

  const invalidateLead = () => {
    queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    queryClient.invalidateQueries({ queryKey: ['lead-timeline', leadId] });
    queryClient.invalidateQueries({ queryKey: ['lead-conversion-readiness', leadId] });
  };

  const statusMutation = useMutation({
    mutationFn: (leadStatusId: number) => changeLeadStatus(leadId, leadStatusId),
    onSuccess: invalidateLead,
  });

  const assignMutation = useMutation({
    mutationFn: ({ userId, reason }: { userId: number; reason?: string }) =>
      leadQuery.data?.assignedTo
        ? transferLead(leadId, userId, reason)
        : assignLead(leadId, userId),
    onSuccess: () => {
      invalidateLead();
      setAssignOpen(false);
      setAssignError(null);
    },
    onError: (err) => setAssignError(extractErrorMessage(err)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteLead(leadId),
    onSuccess: () => navigate(back.to),
    onError: (err) => setDeleteError(extractErrorMessage(err)),
  });

  // The actual "Convert to Student" action (Convert-to-Student workflow
  // correction) — reuses the existing, already backend-gated finalize
  // endpoint (Personal Appointment / Registration Fee / permission are all
  // enforced there, not just by hiding this button) rather than a new
  // one-shot creation call.
  const finalizeMutation = useMutation({
    mutationFn: () => finalizeStudent(leadQuery.data!.student!.id),
    onSuccess: (student) => {
      invalidateLead();
      setConvertConfirmOpen(false);
      setConvertConfirmError(null);
      navigate(`/students/${student.id}`);
    },
    onError: (err) => setConvertConfirmError(extractErrorMessage(err)),
  });

  const createPaMutation = useMutation({
    mutationFn: (payload: { appointmentDate?: string; notes?: string; counselorIds?: number[] }) =>
      createPersonalAppointment({ leadId, ...payload }),
    onSuccess: (pa) => {
      invalidateLead();
      setPaOpen(false);
      setPaError(null);
      // Exact wording per spec for the synced case; honest, non-alarming
      // variants for "not connected yet" / "sync failed" — creation
      // itself always succeeds either way (see personalAppointments
      // .service.ts's syncNewAppointmentToGoogleCalendar).
      setPaSuccessMessage(
        pa.googleCalendarSyncStatus === 'SYNCED'
          ? 'Personal Appointment created and Google Calendar updated.'
          : pa.googleCalendarSyncStatus === 'FAILED'
            ? 'Personal Appointment created. Google Calendar sync failed — see the timeline for details.'
            : 'Personal Appointment created. Google Calendar not connected.',
      );
    },
    onError: (err) => setPaError(extractErrorMessage(err)),
  });

  const completePaMutation = useMutation({
    mutationFn: (paId: number) => completePersonalAppointment(paId),
    onSuccess: invalidateLead,
  });

  const approveMutation = useMutation({
    mutationFn: () => approveLead(leadId),
    onSuccess: () => {
      invalidateLead();
      setApproveError(null);
    },
    onError: (err) => setApproveError(extractErrorMessage(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => rejectLead(leadId, reason),
    onSuccess: () => {
      invalidateLead();
      setRejectOpen(false);
      setRejectError(null);
    },
    onError: (err) => setRejectError(extractErrorMessage(err)),
  });

  if (leadQuery.isLoading) return <CircularProgress sx={{ m: 3 }} />;
  if (!leadQuery.data) return <Alert severity="error" sx={{ m: 3 }}>Lead not found.</Alert>;

  const lead = leadQuery.data;
  const canAssign = hasPermission('leads', 'assign');
  const canTransfer = hasPermission('leads', 'transfer');
  // Super Admin only (Phase 66) — deliberately not `hasPermission('leads',
  // 'delete')`, since Director also holds that permission via the 'ALL'
  // shortcut (see permissions.ts) but must not see this destructive action.
  const canDelete = user?.role.name === 'Super Admin';
  const canRegisterStudent = hasPermission('students', 'create');
  const canCreatePa = hasPermission('personal_appointments', 'create');
  const canCompletePa = hasPermission('personal_appointments', 'complete');
  const canApprove = hasPermission('leads', 'approve');
  const isPending = lead.approvalStatus === 'PENDING_APPROVAL';
  const readiness = readinessQuery.data;

  // Convert-to-Student workflow correction: pre-fills a fresh application
  // from data already captured on the Lead, so the counselor/student never
  // has to retype it into the Student Application Wizard.
  const leadPrefill: Partial<WizardValues> = {
    firstName: lead.firstName,
    lastName: lead.lastName,
    email: lead.email ?? '',
    phone: lead.phone,
    gender: lead.gender ?? '',
    dateOfBirth: lead.dateOfBirth ? lead.dateOfBirth.slice(0, 10) : '',
    nationalityCountryId: lead.nationalityCountry?.id ?? '',
    address: lead.address ?? '',
    city: lead.city ?? '',
    postalCode: lead.postalCode ?? '',
    telephone: lead.telephone ?? '',
    schoolName: lead.schoolName ?? '',
    olType: lead.olType ?? '',
    olYear: lead.olYear ?? '',
    alType: lead.alType ?? '',
    alYear: lead.alYear ?? '',
    alStream: lead.alStream ?? '',
    overallGpa: lead.overallGpa ? Number(lead.overallGpa) : '',
    countryId: lead.country?.id ?? '',
    preferredQualificationLevelId: lead.preferredQualificationLevel?.id ?? '',
    preferredUniversityId: lead.preferredUniversity?.id ?? '',
    // Student Application correction: the wizard's Semester/Intake field
    // is free text now — carry the Lead's own chosen Intake's name over
    // as a sensible starting value (editable from there), never invented
    // when the Lead never set one.
    preferredIntakeLabel: lead.preferredIntake?.name ?? '',
    // Student Application correction: Expected Budget/Currency removed
    // from Step 5's UI — no longer part of WizardValues, so no longer
    // prefilled here either. The backend's own createStudent still
    // inherits the Lead's budget/currency automatically when the Student
    // Application payload omits them (see students.service.ts), so
    // attribution is preserved without this frontend prefill.
    scholarshipRequired: lead.scholarshipRequired,
    fundingMethod: lead.fundingSource ?? '',
    sponsorDetails: lead.sponsorDetails ?? '',
    fatherName: lead.fatherName ?? '',
    fatherPhone: lead.fatherPhone ?? '',
    fatherOccupation: lead.fatherOccupation ?? '',
    fatherOrganization: lead.fatherOrganization ?? '',
    fatherOfficePhone: lead.fatherOfficePhone ?? '',
    fatherEmail: lead.fatherEmail ?? '',
    motherName: lead.motherName ?? '',
    motherPhone: lead.motherPhone ?? '',
    motherOccupation: lead.motherOccupation ?? '',
    motherOrganization: lead.motherOrganization ?? '',
    motherOfficePhone: lead.motherOfficePhone ?? '',
    motherEmail: lead.motherEmail ?? '',
    guardianName: lead.guardianName ?? '',
    guardianRelationship: lead.guardianRelationship ?? '',
    guardianPhone: lead.guardianPhone ?? '',
    guardianEmail: lead.guardianEmail ?? '',
  };

  const openWizard = () => {
    setResumeStudentId(lead.student?.id ?? null);
    setWizardOpen(true);
  };

  return (
    <Box sx={{ p: 3 }}>
      <DetailHeroHeader
        icon={ContactsIcon}
        color="#5B2DA3"
        backLabel={back.label}
        backTo={back.to}
        title={`${lead.firstName} ${lead.lastName}`}
        chips={
          <>
            <Chip size="small" label={`Source: ${lead.leadSource.name}`} />
            {lead.adIdName && <Chip size="small" label={`Ad ID: ${lead.adIdName}`} />}
            {lead.branch && <Chip size="small" label={`Branch: ${lead.branch.name}`} />}
            {lead.country && <Chip size="small" label={`Interested in: ${lead.country.name}`} />}
            <Chip
              size="small"
              label={lead.assignedTo ? `Assigned: ${lead.assignedTo.firstName} ${lead.assignedTo.lastName}` : 'Unassigned'}
              color={lead.assignedTo ? 'default' : 'warning'}
            />
            <Chip
              size="small"
              label={APPROVAL_LABELS[lead.approvalStatus]}
              color={lead.approvalStatus === 'APPROVED' ? 'success' : lead.approvalStatus === 'REJECTED' ? 'error' : 'warning'}
            />
          </>
        }
        actions={
          <Stack direction="row" spacing={1}>
            {isPending && canApprove && (
              <>
                <Tooltip title="Approve lead">
                  <IconButton color="success" onClick={() => approveMutation.mutate()}>
                    <CheckCircleIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject lead">
                  <IconButton
                    color="error"
                    onClick={() => {
                      setRejectError(null);
                      setRejectOpen(true);
                    }}
                  >
                    <CancelIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {canDelete && (
              <Tooltip title="Delete lead">
                <IconButton
                  onClick={() => {
                    setDeleteError(null);
                    setDeleteConfirmOpen(true);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {lead.leadNumber} · {lead.phone} {lead.email ? `· ${lead.email}` : ''}
        </Typography>

        {approveError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApproveError(null)}>
            {approveError}
          </Alert>
        )}
        {isPending && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This lead is pending approval and can't be progressed (follow-ups/status changes) until a Lead Manager
            reviews it.
          </Alert>
        )}
        {lead.approvalStatus === 'REJECTED' && lead.rejectionReason && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Rejected: {lead.rejectionReason}
          </Alert>
        )}
        {/* Note entered with the lead (Quick Lead "Note"). */}
        {lead.notes && (
          <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }} data-testid="lead-note">
            <Typography variant="caption" color="text.secondary" display="block">
              Note
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {lead.notes}
            </Typography>
          </Box>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            select
            label="Status"
            size="small"
            sx={{ width: 200 }}
            value={lead.leadStatus.id}
            onChange={(e) => statusMutation.mutate(Number(e.target.value))}
          >
            {statusesQuery.data?.map((s) => (
              <MenuItem key={s.id as number} value={s.id as number}>
                {String(s.name)}
              </MenuItem>
            ))}
          </TextField>

          {(canAssign || canTransfer) && (
            <Button
              variant="outlined"
              onClick={() => {
                setAssignError(null);
                setAssignOpen(true);
              }}
            >
              {lead.assignedTo ? 'Transfer' : 'Assign'}
            </Button>
          )}

          {lead.student && !lead.student.isDraft ? (
            <Button variant="outlined" onClick={() => navigate(`/students/${lead.student!.id}`)}>
              View student ({lead.student.studentNumber})
            </Button>
          ) : (
            lead.approvalStatus === 'APPROVED' &&
            (() => {
              const pa = lead.personalAppointment;
              if (!pa) {
                return (
                  canCreatePa && (
                    <Button
                      variant="contained"
                      onClick={() => {
                        setPaError(null);
                        setPaOpen(true);
                      }}
                    >
                      Convert to Personal Appointment
                    </Button>
                  )
                );
              }
              if (pa.status === 'SCHEDULED') {
                return (
                  canCompletePa && (
                    <Button variant="contained" onClick={() => completePaMutation.mutate(pa.id)}>
                      Complete Personal Appointment
                    </Button>
                  )
                );
              }
              // COMPLETED: no button here — Application / Registration Fee /
              // Student Conversion all move to the "Next steps" panel below,
              // instead of a single "Start Application" trigger that used to
              // jump straight into the old (removed) conversion form.
              return null;
            })()
          )}
        </Stack>

        {lead.personalAppointment && (!lead.student || lead.student.isDraft) && (
          <Alert
            severity={lead.personalAppointment.status === 'COMPLETED' ? 'success' : lead.personalAppointment.status === 'CANCELLED' ? 'warning' : 'info'}
            sx={{ mt: 2 }}
          >
            <Stack spacing={0.5}>
              <Box>
                Personal Appointment: {PA_STATUS_LABELS[lead.personalAppointment.status]}
                {lead.personalAppointment.appointmentDate &&
                  ` · ${new Date(lead.personalAppointment.appointmentDate).toLocaleString()}`}
                {lead.personalAppointment.notes && ` · ${lead.personalAppointment.notes}`}
              </Box>
              {lead.personalAppointment.counselors.length > 0 && (
                <Box sx={{ fontSize: '0.8125rem' }}>
                  Counselors:{' '}
                  {lead.personalAppointment.counselors
                    .map((c) => `${c.user.firstName} ${c.user.lastName}${c.isPrimary ? ' (primary)' : ''}`)
                    .join(', ')}
                </Box>
              )}
              {lead.personalAppointment.googleCalendarSyncStatus && (
                <Chip
                  size="small"
                  sx={{ alignSelf: 'flex-start' }}
                  color={
                    lead.personalAppointment.googleCalendarSyncStatus === 'SYNCED'
                      ? 'success'
                      : lead.personalAppointment.googleCalendarSyncStatus === 'FAILED'
                        ? 'error'
                        : 'default'
                  }
                  label={
                    lead.personalAppointment.googleCalendarSyncStatus === 'SYNCED'
                      ? 'Google Calendar: Synced'
                      : lead.personalAppointment.googleCalendarSyncStatus === 'FAILED'
                        ? 'Google Calendar: Sync failed'
                        : 'Google Calendar: Not connected'
                  }
                />
              )}
            </Stack>
          </Alert>
        )}

        {lead.approvalStatus === 'APPROVED' &&
          lead.personalAppointment?.status === 'COMPLETED' &&
          (!lead.student || lead.student.isDraft) && (
            <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Next steps
              </Typography>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">
                    Application:{' '}
                    {readiness?.application.completed
                      ? 'Completed ✓'
                      : lead.student
                        ? 'In progress'
                        : 'Not started'}
                  </Typography>
                  {canRegisterStudent && (
                    <Button size="small" variant="outlined" onClick={openWizard}>
                      {!lead.student ? 'Start Application' : readiness?.application.completed ? 'Edit Application' : 'Continue Application'}
                    </Button>
                  )}
                </Stack>

                {/* Application form (2026-11-06): the student completes it in the
                    portal, or the counsellor with the form above — one application. */}
                <LeadApplicationFormPanel
                  leadId={lead.id}
                  canManage={hasPermission('students', 'update')}
                  onCounselorMethod={openWizard}
                  onChanged={invalidateLead}
                />

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">
                      Registration Fee
                      {readiness?.registrationFee.requiredAmount != null
                        ? ` (${readiness.registrationFee.currency?.symbol ?? readiness.registrationFee.currency?.code ?? ''} ${readiness.registrationFee.requiredAmount})`
                        : ''}
                    </Typography>
                    {readiness && <RegistrationPaymentChip status={readiness.registrationFee.registrationPaymentStatus} prefix="Payment" />}
                    <Typography variant="caption" color="text.secondary">
                      Optional to register — can be recorded later.
                    </Typography>
                  </Stack>
                  {lead.student && (
                    <Button size="small" variant="outlined" onClick={() => navigate(`/students/${lead.student!.id}`)}>
                      View Payment
                    </Button>
                  )}
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">Documents</Typography>
                  {lead.student && (
                    <Button size="small" variant="outlined" onClick={() => navigate(`/students/${lead.student!.id}`)}>
                      View Checklist
                    </Button>
                  )}
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2">Student Conversion</Typography>
                  {readiness?.readyToConvert ? (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        setConvertConfirmError(null);
                        setConvertConfirmOpen(true);
                      }}
                    >
                      Convert to Student
                    </Button>
                  ) : (
                    <Chip size="small" label={`Locked${readiness?.blockReason ? ` — ${readiness.blockReason}` : ''}`} />
                  )}
                </Stack>
              </Stack>
            </Box>
          )}
      </DetailHeroHeader>

      <Tabs value={tab} onChange={(_e: SyntheticEvent, v: string) => setTab(v)} variant="scrollable">
        <Tab label="Timeline" value="timeline" />
        <Tab label="Follow-ups" value="follow-ups" />
        <Tab label="Calls" value="calls" />
        <Tab label="WhatsApp" value="whatsapp" />
        <Tab label="Email" value="email" />
        <Tab label="Attachments" value="attachments" />
      </Tabs>

      {tab === 'timeline' && <TimelineTab leadId={leadId} />}
      {tab === 'follow-ups' && <FollowUpsTab leadId={leadId} />}
      {tab === 'calls' && <CallsTab leadId={leadId} phone={lead.phone} />}
      {tab === 'whatsapp' && <WhatsAppTab leadId={leadId} />}
      {tab === 'email' && <EmailTab leadId={leadId} hasEmail={!!lead.email} />}
      {tab === 'attachments' && <AttachmentsTab leadId={leadId} />}

      <AssignLeadDialog
        open={assignOpen}
        isReassignment={!!lead.assignedTo}
        errorMessage={assignError}
        onClose={() => setAssignOpen(false)}
        onSubmit={async (userId, reason) => {
          await assignMutation.mutateAsync({ userId, reason });
        }}
      />

      <PersonalAppointmentDialog
        open={paOpen}
        errorMessage={paError}
        onClose={() => setPaOpen(false)}
        onSubmit={async (payload) => {
          await createPaMutation.mutateAsync(payload);
        }}
      />

      <StudentApplicationWizard
        open={wizardOpen}
        leadId={leadId}
        resumeStudentId={resumeStudentId}
        leadPrefill={leadPrefill}
        onClose={() => setWizardOpen(false)}
        onSaved={invalidateLead}
      />

      <ConvertToStudentConfirmDialog
        open={convertConfirmOpen}
        leadName={`${lead.firstName} ${lead.lastName}`}
        readiness={readiness}
        errorMessage={convertConfirmError}
        submitting={finalizeMutation.isPending}
        onClose={() => setConvertConfirmOpen(false)}
        onConfirm={() => finalizeMutation.mutate()}
      />

      <RejectLeadDialog
        open={rejectOpen}
        errorMessage={rejectError}
        onClose={() => setRejectOpen(false)}
        onSubmit={async (reason) => {
          await rejectMutation.mutateAsync(reason);
        }}
      />

      <DeleteLeadConfirmDialog
        open={deleteConfirmOpen}
        leadName={`${lead.firstName} ${lead.lastName}`}
        leadNumber={lead.leadNumber}
        errorMessage={deleteError}
        submitting={deleteMutation.isPending}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={() => deleteMutation.mutate()}
      />

      <Snackbar
        open={!!paSuccessMessage}
        autoHideDuration={6000}
        onClose={() => setPaSuccessMessage(null)}
        message={paSuccessMessage}
      />
    </Box>
  );
};

const extractErrorMessage = (err: unknown): string => {
  if (isAxiosError(err)) {
    return err.response?.data?.message ?? 'Something went wrong.';
  }
  return 'Something went wrong.';
};
